/**
 * App-Web 双向通信类型定义
 * 用于统一 Android TWA 和 iOS PWA 与 Web 端的通信协议
 */

/**
 * 消息类型枚举
 */
export enum MessageType {
  // App → Web 消息
  APP_INIT = "app-init",
  DEVICE_INFO = "device-info",
  /** App → Web：下载任务已开始 */
  DOWNLOAD_STARTED = "download-started",
  /** App → Web：下载任务进度更新 */
  DOWNLOAD_PROGRESS = "download-progress",
  /** App → Web：下载任务完成 */
  DOWNLOAD_COMPLETED = "download-completed",
  /** App → Web：下载任务失败 */
  DOWNLOAD_FAILED = "download-failed",
  /** App → Web：软键盘即将显示（含 height、layoutMode 等） */
  KEYBOARD_WILL_SHOW = "keyboard-will-show",
  /** App → Web：软键盘已显示 */
  KEYBOARD_DID_SHOW = "keyboard-did-show",
  /** App → Web：软键盘即将隐藏 */
  KEYBOARD_WILL_HIDE = "keyboard-will-hide",
  /** App → Web：软键盘已隐藏 */
  KEYBOARD_DID_HIDE = "keyboard-did-hide",
  // Web → App 消息
  PAGE_READY = "page-ready",
  /** H5 通知原生：启动期登录弹窗已接管首屏，可按新门控撤掉 cold-start splash */
  WEB_LOGIN_GATE_READY = "web-login-gate-ready",
  /** H5 通知首屏可交互，可隐藏启动屏（冷启动体验优化） */
  APP_INTERACTIVE_READY = "app-interactive-ready",
  CALL_METHOD = "call-method",
  REQUEST_DEVICE_INFO = "request-app-init",
  REQUEST_PUSH_TOKEN = "request-push-token",
  /** 仅 iOS：请求 ATT 授权状态并回传 IDFA */
  REQUEST_APP_TRACKING_TRANSPARENCY = "request-app-tracking-transparency",
  /** App → Web：用户点击推送后下发推送内容 */
  PUSH_NOTIFICATION_CLICKED = "push-notification-clicked",
  /** App → Web：原生解析完成的 deeplink URL（由 Web 负责参数映射和路由） */
  DEEP_LINK_OPENED = "deep-link-opened",
  /** Web → App：读取最近一次 deep-link-opened 解析结果（含 resultId / businessQuery） */
  GET_LAST_DEEP_LINK = "get-last-deep-link",
  /** App → Web：WebView 重新可交互（支付弹层关闭、Tab 解冻等） */
  SHELL_WEBVIEW_INTERACTIVE = "shell-webview-interactive",
  /** App → Web：应用进入前台（onResume），可刷新权限、恢复轮询等 */
  APP_FOREGROUND = "app-foreground",
  /** App → Web：Chat WebView 打开时携带启动参数 */
  SHELL_CHAT_OPEN = "shell-chat-open",
  /** App → Web：Detail 轻页面收到后 replace 到真实目标路由 */
  DETAIL_SHELL_OPEN = "detail-shell-open",
  /** Web 主动请求 GA4 gaInstanceId/sessionId，弥补注入失败或延迟 */
  REQUEST_GA_INFO = "request-ga-info",
  // Payment 相关消息（Web → App）
  PAYMENT_FETCH_PRODUCTS = "payment-fetch-products",
  PAYMENT_PURCHASE = "payment-purchase",
  PAYMENT_RESTORE = "payment-restore",
  PAYMENT_GET_STATUS = "payment-get-status",
  /** 打开商店/APP 订阅管理页（iOS App Store / Android Google Play） */
  PAYMENT_OPEN_MANAGEMENT = "payment-open-management",
  /** 在系统/外部浏览器打开 URL（如 Stripe 订单管理页），避免在 WebView 内打开 */
  OPEN_URL_EXTERNAL = "open-url-external",
  /** 在 App 内置浏览器（Chrome Custom Tab / Safari View Controller）打开 URL */
  OPEN_URL_INAPP = "open-url-inapp",
  // Payment 相关消息（App → Web）
  PAYMENT_PRODUCTS = "payment-products",
  PAYMENT_PURCHASE_SUCCESS = "payment-purchase-success",
  PAYMENT_PURCHASE_RESULT = "payment-purchase-result",
  PAYMENT_RESTORE_COMPLETE = "payment-restore-complete",
  PAYMENT_STATUS = "payment-status",
  /** Web → App：关键事件上报，由 App 通过 Firebase Analytics 上报，确保广告归因正常 */
  LOG_EVENT = "log-event",
  /** Web → App：Adjust 事件上报（eventToken / eventKey） */
  ADJUST_TRACK_EVENT = "adjust-track-event",
  /** Web → App：Adjust 订阅上报（iOS App Store / Android Play Store） */
  ADJUST_TRACK_SUBSCRIPTION = "adjust-track-subscription",
  /** Web → App：Adjust iOS SKAdNetwork 转化值更新（仅 iOS 生效） */
  ADJUST_UPDATE_SKAN_CONVERSION_VALUE = "adjust-update-skan-conversion-value",
  ADJUST_GET_ADID = "adjust-get-adid",
  /** 仅 Android WebView：Google Advertising ID（GAID）；与请求头 OS-Advertising-Id 对齐，非 Adjust ADID */
  OS_GET_ADVERTISING_ID = "os-get-advertising-id",
  ADJUST_GET_ATTRIBUTION = "adjust-get-attribution",
  ADJUST_SET_EXTERNAL_DEVICE_ID = "adjust-set-external-device-id",
  NOTIFICATION_PERMISSION = "notification-permission",
  REQUEST_NOTIFICATION_PERMISSION = "request-notification-permission",
  /** Web → App：查询麦克风权限 / 打开应用设置（与 Android BridgeManager#MSG_TYPE_VOICE_PERMISSION 对齐） */
  VOICE_PERMISSION = "voice-permission",
  /** Web → App：请求唤起系统麦克风（RECORD_AUDIO）授权流程（与 Android BridgeManager#MSG_TYPE_REQUEST_VOICE_PERMISSION 对齐） */
  REQUEST_VOICE_PERMISSION = "request-voice-permission",
  REQUEST_APP_RATE = "request-app-rate",
  /** Web → App：跨容器广播 */
  CONTAINER_BROADCAST = "container-broadcast",
  /** Web → App：容器控制（见 {@link ContainerControlAction}） */
  CONTAINER_CONTROL = "container-control",
  /** Web → App：切换三 Tab 壳当前可见主 Tab（0=Invest, 1=Explore, 2=Tracker） */
  SWITCH_SHELL_TAB = "switch-shell-tab",
  /** Web → App：自底部弹出 Chat 常驻 WebView（modal） */
  PRESENT_SHELL_CHAT = "present-shell-chat",
  /** Web → App：关闭 Chat modal，回到弹出前的主 Tab */
  DISMISS_SHELL_CHAT = "dismiss-shell-chat",
  /** Web → App：Chat WebView loading 状态（后台低频唤醒） */
  SHELL_CHAT_LOADING = "shell-chat-loading",
  /** Web → App：登出后各壳 Tab WebView 重载为各自 seed */
  SYNC_SHELL_TABS_AFTER_LOGOUT = "sync-shell-tabs-after-logout",
  /** Web → App：语言切换后其它 Tab reload 对齐 locale */
  SYNC_SHELL_TABS_AFTER_LOCALE_CHANGE = "sync-shell-tabs-after-locale-change",
  /** Web → App：拉取未送达的长操作结果（首期仅支付） */
  BRIDGE_GET_PENDING_OUTCOMES = "bridge-get-pending-outcomes",
  /** Web → App：确认已处理某 operation，原生可清理 Outbox */
  BRIDGE_ACK_OUTCOME = "bridge-ack-outcome",
  /**
   * Web → App：设置当前前台 WebView 对应原生容器的状态栏样式。
   * payload: `{ style: 'light' | 'dark' | 'auto' }`（light=深色图标，dark=浅色图标）。
   */
  SET_STATUS_BAR_STYLE = "set-status-bar-style",
  /** Web → App：请求原生渲染付费墙；resolve(true) = 用户已完成购买 */
  OPEN_NATIVE_PAYWALL = "open-native-paywall",
  /** Web → App：拉起原生 EdChat（question 预填 / autoSubmit 自动发 / autoRenew 新会话） */
  OPEN_NATIVE_CHAT = "open-native-chat",
  /**
   * Web → App：拉起原生 Plaid 连接链（七步引导弹窗 + broker 列表 + SDK + My accounts Confirm）。
   * resolve(data) = `{ action: "confirm" | "close" | "error" }`（对齐 `HandleImportClickChainResult`）。
   */
  OPEN_NATIVE_PLAID_CONNECT = "open-native-plaid-connect",
  /** Web → App：Daily market summary 已读落定 → 原生 DataStore（§2.4 hybrid 壳同步） */
  SYNC_DAILY_MARKET_READ = "sync-daily-market-read",
  /**
   * Web → App：report 结束页「Turn on notifications」→ 原生推送授权流程
   * （系统弹窗 → 后端 OPEN；永拒跳系统设置页）。授权流程完成（granted/denied 均然）
   * 由原生关闭 report webview 回 Brief（S1.5）；granted 的 report_push_granted 在原生侧上报。
   */
  NOTIFY_PERMISSION_REQUEST = "notify-permission-request",
  /** Web → App：report 结束页「Explore Invest Opportunity」→ 关 webview + 原生路由（payload 见 {@link NativeNavPayload}） */
  NATIVE_NAV = "native-nav",
  /**
   * 声明「本 WebView 页面命中了某个原生弹层的展示时机」（EDG-433）。
   *
   * **只声明，不请求结果** —— 裁决（冷却 / 优先级 / 频次帽）、计时、记账全在原生。
   * 原生只回 ack，不回裁决结果；web 不记账也就不需要知道弹没弹。
   */
  REQUEST_NATIVE_SURFACE = "request-native-surface",
  /**
   * App → Web：业务载荷补推（namespaced bag，对齐 documentStart `window.__ED_NATIVE_PAYLOAD__`）。
   * 池复用时 documentStart 不重跑，靠 `page-ready` 后这条修复。见契约 §3.2。
   */
  NATIVE_PAYLOAD = "native-payload",
}

/**
 * `container-control` 消息的 `action` 字段（与 iOS / Android `BridgeManager` 对齐）。
 */
export enum ContainerControlAction {
  /**
   * 打开原生二级 Detail 容器并加载 `url`（payload 需带 `url`）。
   * Web 侧入口：`Bridge.openDetailInNative`。
   */
  OPEN_DETAIL = "open-detail",
  /**
   * 关闭当前 Detail 叠层，回到三 Tab 壳（不指定目标 URL）。
   * Web 侧入口：`Bridge.closeDetailInNative`。
   */
  CLOSE_DETAIL = "close-detail",
  /**
   * 先关闭 Detail，再在对应主 Tab 壳 WebView 加载 `url`（payload 需带 `url`）。
   * 用于 Detail 内导航回 Invest / Explore / Tracker 等壳路径。
   * Web 侧入口：`Bridge.popDetailAndNavigateShell`。
   */
  POP_DETAIL_NAVIGATE_SHELL = "pop-detail-navigate-shell",
  /**
   * Detail 内运行时同步是否允许手势 / 系统返回关闭容器（payload 需带 `enabled`）。
   * Web 侧入口：`Bridge.setDetailGestureBack`。
   */
  SET_DETAIL_GESTURE_BACK = "set-detail-gesture-back",
}

/** `set-status-bar-style` payload：`light` = 深色图标；`dark` = 浅色图标；`auto` = 原生默认。 */
export type NativeStatusBarStyle = "light" | "dark" | "auto";

/** `native-nav` 消息的目标原生落点（report 结束页 B2 桥；增量扩展须双端同步）。 */
export type NativeNavTarget = "invest-opportunity";

/** `native-nav` 消息 data：`{ target }`。 */
export interface NativeNavPayload {
  target: NativeNavTarget;
}

/** `open-detail` 可选参数；默认 `true`（允许关闭 Detail）。 */
export type OpenDetailInNativeOptions = {
  gestureBackEnabled?: boolean;
};

/**
 * `container-broadcast` 消息的 `event` 字段（Web 发出 → 原生中继 → 其它 WebView）。
 */
export enum ContainerBroadcastEvent {
  /**
   * 当前 WebView 内会员/权益已变更；其它 Tab / Detail 应失效并重新拉取会员相关接口。
   * 接收侧见 `useContainerBroadcastStateSync` 与 `invalidateMemberQueries`。
   */
  MEMBER_UPDATED = "member-updated",
  /**
   * 当前 WebView 内 Tracker 阅读水印已变更；其它 Tab / Detail 应刷新阅读状态。
   * 接收侧见 `useTrackerReadWatermark`。
   */
  TRACKER_READ_WATERMARK_UPDATED = "tracker-read-watermark-updated",
  /**
   * 当前 WebView 已完成 OSP/邮箱登录且共享缓存就绪；其它 Tab / Detail 应从 localStorage 恢复会话。
   * 发送侧见 `broadcastAuthSessionUpdated`；接收侧见 `useCrossWebViewAuthSessionSync`。
   */
  AUTH_SESSION_UPDATED = "auth-session-updated",
  /**
   * 当前可见 WebView 已展示指定全局 modal；其它 Tab / Detail 本会话不再弹同一 modalKey。
   * 发送侧见 `broadcastGlobalAppModalClaimed`；接收侧见 `useGlobalAppModalLimit`。
   */
  GLOBAL_APP_MODAL_CLAIMED = "global-app-modal-claimed",
}

/**
 * 平台类型
 */
export type Platform = "android" | "ios" | "web";

/**
 * 设备信息
 */
interface DeviceInfo {
  model: string;
  osVersion: string;
  manufacturer?: string; // Android only
  /** 原生 Firebase Analytics App Instance ID（与 GA 日志中 app_instance_id 一致，按此 ID 可在 GA 中查到对应上报） */
  nativeDeviceId?: string;
}

/**
 * App 功能特性支持标识
 * 用于 Web 端判断当前 App 版本是否支持某些功能
 */
export interface AppFeatures {
  /** 沉浸式导航栏 */
  immersiveNavbar?: boolean;
  /** 是否支持原生支付（IAP/Google Billing）。老版本 App 未注入该字段时，Web 端应降级走 Stripe/H5 支付 */
  nativePayment?: boolean;
  /** 是否支持文件上传（选择附件 / 拖拽上传） */
  fileUploadAccess?: boolean;
  /** 是否支持文件下载（附件下载 / 图片预览） */
  fileDownloadAccess?: boolean;
  /** 是否拥有麦克风（语音输入）权限 */
  voiceAccess?: boolean;
  /**
   * 原生是否承载 push 授权引导（EDG-433）。
   *
   * 为 true 时 **web 侧不渲染自己那份引导** —— App 内的弹层机制统一在 kmp-app，
   * web 只在仍留在 WebView 的页面里发 `REQUEST_NATIVE_SURFACE` 声明「命中了某个时机」，
   * 裁决与展示全在原生。老版本 App 未注入该字段时按 undefined 处理，web 保持现状行为。
   *
   * 契约见 `docs/engineering/native-web-bridge-contract.md`。
   */
  nativePushPrimer?: boolean;
  /**
   * 原生是否承载 app 评分弹层（EDG-433）。
   *
   * 为 true 时 **web 侧不渲染 `AppRatingSuggestionModalContainer`** —— App 内的弹层机制统一在
   * kmp-app（原生那份走 Surface Manager 的冷却/优先级/频次帽）。老版本 App 未注入该字段时按
   * undefined 处理，web 保持现状行为（旧壳里 web 那份仍是唯一实现）。
   *
   * 契约见 `docs/engineering/native-web-bridge-contract.md`。
   */
  nativeAppRating?: boolean;
  /**
   * 原生 Adjust 是否已配置可上报（appToken 非空且 enabled）；sandbox 表示非生产 Adjust 环境。
   * 业务侧应优先用 eventKey + 原生 eventTokens 映射，避免在 Web 写死 eventToken。
   */
  adjust?: {
    reportingEnabled?: boolean;
    sandbox?: boolean;
  };
  /**
   * 是否支持键盘 inset Bridge + CSS 注入（Phase 1）。
   * 仅当原生显式 `true` 时启用；老 App 未注入时应保持 native-resize 降级。
   */
  /** 仅 iOS 为 true；Android 为 false，Web 不监听 keyboard-* */
  keyboardInsetsBridge?: boolean;
  /**
   * Phase 2：原生不缩小 WebView；Web 根据 Bridge 高度写 `--keyboard-inset-bottom` 做底部 padding。
   * 需与 `keyboardInsetsBridge === true` 同时使用。
   */
  keyboardWebInset?: boolean;
  /**
   * 是否由原生承载会员付费墙 UI。
   * 未注入（老 App / 浏览器）→ Web 继续渲染自己的 PaywallModalContent，行为完全不变。
   */
  nativePaywall?: boolean;
  /**
   * 是否由原生承载 EdChat。
   * 未注入（老 App / 浏览器）→ Web 继续打开自己的 ChatModal，行为完全不变。
   */
  nativeChat?: boolean;
  /**
   * 是否由原生承载 Plaid 连接链（broker 列表 / 引导弹窗 / My accounts Confirm）。
   * 未注入（老 App / 浏览器）→ Web 继续渲染自己的 PlaidConnectFlowModal，行为完全不变。
   */
  nativePlaidConnect?: boolean;
}

/** 键盘布局策略：由原生在 app-init / keyboard 消息中声明 */
export type KeyboardLayoutMode = "native-resize" | "web-inset";

/**
 * App → Web 键盘 inset 消息体（keyboard-will-show / keyboard-did-show 等）
 */
export interface KeyboardInsetsPayload {
  visible: boolean;
  /** 键盘占用高度（px，逻辑像素） */
  height: number;
  /** 与原生键盘动画一致的时长（ms） */
  duration?: number;
  layoutMode?: KeyboardLayoutMode;
}

/**
 * App 初始化数据
 */
export interface AppInitData {
  platform: Platform;
  appVersion: string;
  buildNumber?: string; // iOS/Android build number
  bundleId?: string; // iOS bundle identifier / Android package name
  appName?: string; // App display name
  deviceInfo: DeviceInfo;
  /** 功能特性支持标识（新版本 App 才有） */
  features?: AppFeatures;
  /** 原生 Firebase Analytics App Instance ID（与 GA 日志中 app_instance_id 一致，按此 ID 可在 GA 中查到对应上报） */
  nativeDeviceId?: string;
}

/**
 * 基础消息结构
 */
export interface BaseMessage {
  type: string;
  id?: string;
  data?: any;
}

/**
 * App → Web 消息
 */

/**
 * Web → App 消息
 */

/**
 * log-event 消息的 data 结构（Web 发给 App，由 App 通过 Firebase Analytics 上报）
 */

export interface AdjustTrackSubscriptionPayload {
  // common
  price: string | number;
  currency: string;
  callbackParams?: Record<string, string | number | boolean>;
  partnerParams?: Record<string, string | number | boolean>;
  // iOS
  transactionId?: string;
  transactionDateMs?: number;
  salesRegion?: string;
  // Android
  sku?: string;
  orderId?: string;
  signature?: string;
  purchaseToken?: string;
  purchaseTimeMs?: number;
}

export interface AdjustUpdateSkanConversionValuePayload {
  conversionValue: number;
  coarseValue?: "low" | "medium" | "high";
  lockWindow?: boolean;
}

/** `os-get-advertising-id` 响应 data（仅 Android Bridge 实现） */
export interface OsGetAdvertisingIdData {
  advertisingId?: string | null;
  limitAdTracking?: boolean;
  platform?: string;
}

/** `request-app-tracking-transparency` 响应 data（iOS） */
export interface OsAppTrackingTransparencyData {
  authorizationStatus?: number;
  idfa?: string | null;
  idfaAvailable?: boolean;
}

/** `deep-link-opened` / `get-last-deep-link` 消息 data：原生已解析的业务深链 */
export interface DeepLinkOpenedData {
  url: string;
  originalUrl?: string;
  source?: string;
  platform?: "android" | "ios";
  route?: string;
  resultId?: string;
  businessQuery?: Record<string, string>;
}

/**
 * adjust-track-event 的 data（与 iOS/Android Bridge 一致）
 */
export interface AdjustTrackEventPayload {
  /** 与 eventKey 二选一；优先使用。一般不在业务写死，推荐 eventKey + 原生配置映射 */
  eventToken?: string;
  /** 映射到 iOS app-config / Android twa-manifest 的 eventTokens 键 */
  eventKey?: string;
  deduplicationId?: string;
  orderId?: string;
  callbackParams?: Record<string, string | number | boolean>;
  partnerParams?: Record<string, string | number | boolean>;
  revenue?: number;
  currency?: string;
}

/** adjust-get-attribution 成功时 data 中除标准字段外的归因键 */
export interface AdjustAttributionData {
  trackerToken?: string;
  trackerName?: string;
  network?: string;
  campaign?: string;
  adgroup?: string;
  creative?: string;
  clickLabel?: string;
}

/**
 * Bridge `error.code` 约定值（与 Android `BridgeManager` / `BillingManager`、iOS `BridgeManager` 对齐）。
 * 新增原生错误码时请同步补充本联合类型；`BILLING_UNKNOWN_*` 对应 Android 未映射的 Billing 响应码后缀。
 */
export type BridgeErrorCode =
  /** 仅传一条文案时的兜底码（双端四参/字符串 sendResponse） */
  | "BRIDGE_ERROR"
  /** 旧版或缺失 errorCode 时的兜底（优先使用下方具体 BILLING_*） */
  | "BILLING_ERROR"
  /** Bridge：Billing 未初始化 */
  | "PAYMENT_BILLING_NOT_INITIALIZED"
  /** Bridge：未传 productIds */
  | "PAYMENT_PRODUCT_IDS_REQUIRED"
  /** Bridge：组装产品列表 JSON 失败 */
  | "PAYMENT_PRODUCTS_RESPONSE_BUILD_FAILED"
  /** Bridge：Context 非 Activity，无法调起购买 */
  | "PAYMENT_CONTEXT_NOT_ACTIVITY"
  /** Bridge：productId 无效 */
  | "PAYMENT_INVALID_PRODUCT_ID"
  /** Bridge：组装恢复购买响应失败 */
  | "PAYMENT_RESTORE_RESPONSE_BUILD_FAILED"
  /** Billing：未连接 Play 商店 */
  | "BILLING_NOT_CONNECTED"
  /** Billing：productIds 为空 */
  | "BILLING_INVALID_PRODUCT_IDS"
  /** Billing：本地无该产品详情 */
  | "BILLING_PRODUCT_NOT_FOUND"
  /** Billing：组装订阅状态 JSON 失败 */
  | "BILLING_STATUS_SERIALIZE_FAILED"
  /** Google Play BillingResponseCode.USER_CANCELED；iOS StoreKit 用户取消 */
  | "BILLING_USER_CANCELED"
  /** StoreKit 2：交易待批准（Ask to Buy / 延期支付等）；Android 无对应态时可忽略 */
  | "BILLING_PURCHASE_PENDING"
  /** Google Play BillingResponseCode.SERVICE_UNAVAILABLE */
  | "BILLING_SERVICE_UNAVAILABLE"
  /** Google Play BillingResponseCode.BILLING_UNAVAILABLE */
  | "BILLING_UNAVAILABLE"
  /** Google Play BillingResponseCode.ITEM_UNAVAILABLE */
  | "BILLING_ITEM_UNAVAILABLE"
  /** Google Play BillingResponseCode.DEVELOPER_ERROR */
  | "BILLING_DEVELOPER_ERROR"
  /** Google Play BillingResponseCode.ERROR */
  | "BILLING_INTERNAL_ERROR"
  /** Android：`BILLING_UNKNOWN_` + 数字响应码 */
  | `BILLING_UNKNOWN_${string}`;

/**
 * Bridge 请求失败时的错误载荷（与 Android/iOS sendResponse 对齐）
 */
export interface BridgeErrorPayload {
  code: BridgeErrorCode;
  errMsg: string;
}

/**
 * 响应消息结构
 */
export interface ResponseMessage {
  id: string;
  success: boolean;
  data?: any;
  /** 失败时为 { code, errMsg }；仅兼容旧版时可能为 string */
  error?: string | BridgeErrorPayload;
}

// ========== Payment Bridge 消息 data 结构（与 PAYMENT_FLOW_AND_GAPS 对齐） ==========

/** payment-fetch-products：Web → App，产品 ID 由前端从 GET /products 的 payment_configs 取得，必传 */
export type AndroidBillingProductType = "SUBS" | "INAPP";

export interface PaymentFetchProductItem {
  productId: string;
  productType: AndroidBillingProductType;
}

export interface PaymentFetchProductsData {
  /** 新格式：每个商品可独立传 productType（推荐） */
  products?: PaymentFetchProductItem[];
  /** 兼容字段：给所有 productIds 使用同一种类型；默认 SUBS */
  productType?: AndroidBillingProductType;
  /** 兼容旧格式：仅 productIds（默认按 SUBS 处理） */
  productIds?: string[];
}

export interface PaymentRestoreData {
  productType?: AndroidBillingProductType;
}

export interface PaymentGetStatusData {
  productType?: AndroidBillingProductType;
}

/** payment-purchase-success：App → Web，供 Web 调 POST /api/payment/confirm */
export interface PaymentPurchaseSuccessData {
  productId: string;
  /** iOS：传给 confirm 的 transaction_id */
  transactionId?: string;
  /** Android：传给 confirm 的 purchaseToken */
  purchaseToken?: string;
  obfuscatedAccountId?: string;
}
